import React from 'react';
import { useIntl } from '../../util/reactIntl';
import { connect } from 'react-redux';
import classNames from 'classnames';
import {
  ensureUser,
  ensureCurrentUser,
  userDisplayNameAsString,
  userAbbreviatedName,
} from '../../util/data';
import { isUserAuthorized } from '../../util/userHelpers';
import { getSupabaseProfile } from '../../ducks/supabaseProfile.duck';

import { ResponsiveImage, IconBannedUser, NamedLink } from '../../components/';
import profileHolderImage from '../../assets/profileHolder.png';

import css from './Avatar.module.css';

// Responsive image sizes hint
const AVATAR_SIZES = '40px';
const AVATAR_SIZES_MEDIUM = '60px';
const AVATAR_SIZES_LARGE = '96px';

const AVATAR_IMAGE_VARIANTS = [
  // 40x40
  'square-xsmall',

  // 80x80
  'square-xsmall2x',

  // 240x240
  'square-small',

  // 480x480
  'square-small2x',
];

const getProfileImageUrl = profileImage => {
  const variants = profileImage?.attributes?.variants || profileImage?.variants || {};
  return (
    variants.default?.url ||
    variants['square-small']?.url ||
    variants['square-xsmall2x']?.url ||
    variants['square-xsmall']?.url ||
    null
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object?} props.user API entity
 * @param {string} props.renderSizes
 * @param {boolean} props.disableProfileLink
 * @returns {JSX.Element} search icon
 */
const AvatarComponent = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    initialsClassName,
    user,
    renderSizes = AVATAR_SIZES,
    disableProfileLink,
    supabaseAvatarUrl: propSupabaseAvatarUrl, // Allow passing Supabase URL as prop
    supabaseProfile, // Supabase profile from Redux
    currentUserFromState,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const userIsCurrentUser = user && user.type === 'currentUser';
  const avatarUser =
    userIsCurrentUser && !user?.id && currentUserFromState
      ? ensureCurrentUser(currentUserFromState)
      : userIsCurrentUser
      ? ensureCurrentUser(user)
      : ensureUser(user);
  // I.e. the status is active, not pending-approval or banned
  const isUnauthorizedUser = userIsCurrentUser && !isUserAuthorized(user);
  const variant = user?.attributes?.state;
  //'pending-approval'

  const isBannedUser = avatarUser.attributes.banned;
  const isDeletedUser = avatarUser.attributes.deleted;

  const defaultUserDisplayName = isBannedUser
    ? intl.formatMessage({ id: 'Avatar.bannedUserDisplayName' })
    : isDeletedUser
    ? intl.formatMessage({ id: 'Avatar.deletedUserDisplayName' })
    : '';

  const defaultUserAbbreviatedName = '';

  const displayName = userDisplayNameAsString(avatarUser, defaultUserDisplayName);
  const abbreviatedName = userAbbreviatedName(avatarUser, defaultUserAbbreviatedName);
  const rootProps = { className: classes, 'aria-label': displayName };
  const linkProps =
    isUnauthorizedUser && avatarUser.id
      ? {
          name: 'ProfilePageVariant',
          params: { id: avatarUser.id.uuid, variant },
        }
      : avatarUser.id
      ? { name: 'ProfilePage', params: { id: avatarUser.id.uuid } }
      : { name: 'ProfileBasePage' };

  // Check for profile image from Sharetribe
  const profileImageUrl = getProfileImageUrl(avatarUser.profileImage);
  const hasSdkProfileImage = !!(avatarUser.profileImage && avatarUser.profileImage.id);
  const hasProfileImage = hasSdkProfileImage || !!profileImageUrl;

  // Check for Supabase avatar URL - prioritize prop, then publicData, then Supabase profile from Redux
  // Only use Supabase profile if it's for the current user
  const supabaseProfileAvatarUrl = (userIsCurrentUser && supabaseProfile?.avatar_url) || null;
  const supabaseAvatarUrl =
    propSupabaseAvatarUrl ||
    avatarUser.attributes?.profile?.publicData?.avatar_url ||
    supabaseProfileAvatarUrl;
  const hasSupabaseAvatar = !!supabaseAvatarUrl;

  // Use Supabase avatar if available, otherwise fall back to Sharetribe profileImage
  const avatarUrl = hasSupabaseAvatar ? supabaseAvatarUrl : null;
  const hasAnyAvatar = hasProfileImage || hasSupabaseAvatar;

  const profileLinkEnabled = !disableProfileLink;

  if (isBannedUser || isDeletedUser) {
    return (
      <div {...rootProps}>
        <IconBannedUser className={css.bannedUserIcon} />
      </div>
    );
  } else if (hasAnyAvatar && profileLinkEnabled) {
    // Use Supabase avatar URL if available, otherwise use Sharetribe profileImage
    if (hasSupabaseAvatar && avatarUrl) {
      return (
        <NamedLink {...rootProps} {...linkProps}>
          <img
            src={avatarUrl}
            alt={displayName}
            className={css.avatarImage}
            onError={e => {
              e.target.src = profileHolderImage;
            }}
          />
        </NamedLink>
      );
    } else if (hasSdkProfileImage) {
      return (
        <NamedLink {...rootProps} {...linkProps}>
          <ResponsiveImage
            rootClassName={css.avatarImage}
            alt={displayName}
            image={avatarUser.profileImage}
            variants={AVATAR_IMAGE_VARIANTS}
            sizes={renderSizes}
          />
        </NamedLink>
      );
    } else {
      return (
        <NamedLink {...rootProps} {...linkProps}>
          <img
            src={profileImageUrl}
            alt={displayName}
            className={css.avatarImage}
            onError={e => {
              e.target.src = profileHolderImage;
            }}
          />
        </NamedLink>
      );
    }
  } else if (hasAnyAvatar) {
    // Use Supabase avatar URL if available, otherwise use Sharetribe profileImage
    if (hasSupabaseAvatar && avatarUrl) {
      return (
        <div {...rootProps}>
          <img
            src={avatarUrl}
            alt={displayName}
            className={css.avatarImage}
            onError={e => {
              e.target.src = profileHolderImage;
            }}
          />
        </div>
      );
    } else if (hasSdkProfileImage) {
      return (
        <div {...rootProps}>
          <ResponsiveImage
            rootClassName={css.avatarImage}
            alt={displayName}
            image={avatarUser.profileImage}
            variants={AVATAR_IMAGE_VARIANTS}
            sizes={renderSizes}
          />
        </div>
      );
    } else {
      return (
        <div {...rootProps}>
          <img
            src={profileImageUrl}
            alt={displayName}
            className={css.avatarImage}
            onError={e => {
              e.target.src = profileHolderImage;
            }}
          />
        </div>
      );
    }
  } else if (profileLinkEnabled) {
    return (
      <NamedLink {...rootProps} {...linkProps}>
        <img src={profileHolderImage} alt={displayName} className={css.avatarImage} />
      </NamedLink>
    );
  } else {
    return (
      <div {...rootProps}>
        <img src={profileHolderImage} alt={displayName} className={css.avatarImage} />
      </div>
    );
  }
};

// Connect to Redux to get Supabase profile for current user
const mapStateToProps = (state, ownProps) => {
  // Only get Supabase profile if this is the current user
  const userIsCurrentUser = ownProps.user && ownProps.user.type === 'currentUser';
  return {
    currentUserFromState: userIsCurrentUser ? state.user.currentUser : null,
    supabaseProfile: userIsCurrentUser ? getSupabaseProfile(state) : null,
  };
};

const Avatar = connect(mapStateToProps)(AvatarComponent);

export default Avatar;
export { AvatarComponent };

export const AvatarSmall = props => (
  <Avatar
    rootClassName={css.smallAvatar}
    initialsClassName={css.initialsSmall}
    renderSizes={AVATAR_SIZES_MEDIUM}
    {...props}
  />
);
AvatarSmall.displayName = 'AvatarSmall';

export const AvatarMedium = props => (
  <Avatar
    rootClassName={css.mediumAvatar}
    initialsClassName={css.initialsMedium}
    renderSizes={AVATAR_SIZES_MEDIUM}
    {...props}
  />
);
AvatarMedium.displayName = 'AvatarMedium';

export const AvatarLarge = props => (
  <Avatar
    rootClassName={css.largeAvatar}
    initialsClassName={css.initialsLarge}
    renderSizes={AVATAR_SIZES_LARGE}
    {...props}
  />
);
AvatarLarge.displayName = 'AvatarLarge';
